import React from 'react';
import { useTranslation } from 'react-i18next';
import { SystemChip } from './pills';
import { rowActivation } from './rowActivation';

/**
 * InvoiceCardRow (phone) + InvoiceGridRow (desktop table) — #62 next-phase Invoices depth spec (UXeng,
 * cmdr 2026-08-23). Comp columns: Invoice # · Account · Amount · Due Date · Status · Actions. Phone
 * card: title Invoice #, meta-right Amount, meta-line Due, pills = Status + Account.
 */
export interface InvoiceRow {
  id: string;
  number: string;
  amount: number;
  dueDate: string | null;
  status: string;
  companyId: string | null;
  companyName: string | null;
  // B3: the payer is a company XOR a contact — payerName is whichever root this invoice bills.
  payerName?: string | null;
}

export interface InvoiceRowCallbacks {
  selected?: boolean;
  onOpenInvoice: (invoice: InvoiceRow) => void;
}

function formatAmount(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** 🔴 Overdue is COMPUTED, not stored — same discipline as Deal's Last activity (UXeng §2): show the
 *  fact ("due 3d ago"), amber past due, NEVER rose (reserved), never a fabricated "health". Only 'marked_sent'
 *  invoices can be overdue — a draft has nothing outstanding yet, paid/void are resolved. */
export function dueDateLabel(iso: string | null, t: (k: string, opts?: any) => string): string {
  if (!iso) return '—';
  const days = Math.floor((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0) return t('invoicesGrid.dueAgo', { count: Math.abs(days) });
  if (days === 0) return t('invoicesGrid.dueToday');
  return t('invoicesGrid.dueIn', { count: days });
}

export function isOverdue(iso: string | null, status: string): boolean {
  if (!iso || status !== 'marked_sent') return false;
  return new Date(iso).getTime() < Date.now();
}

export const DueDate: React.FC<{ dueDate: string | null; status: string; className?: string }> = ({ dueDate, status, className }) => {
  const { t } = useTranslation('common');
  const overdue = isOverdue(dueDate, status);
  return (
    <span data-testid="invoice-due-date" className={`${className ?? ''} ${overdue ? 'text-warning' : 'text-text-muted'}`} dir="ltr" title={dueDate ?? undefined}>
      {dueDateLabel(dueDate, t)}
    </span>
  );
};

export const InvoiceCardRow: React.FC<{ invoice: InvoiceRow } & InvoiceRowCallbacks> = ({ invoice, selected, onOpenInvoice }) => {
  const { t } = useTranslation('common');
  return (
    <div {...rowActivation(() => onOpenInvoice(invoice))} data-testid="invoice-row" onClick={() => onOpenInvoice(invoice)}
      className={`p-3 rounded-xl bg-surface border ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'} cursor-pointer`}>
      {/* de-fiscalised (PM ruling, 2026-09-10): the PAYER names the record, not the sequence. The
          payer was previously a chip in the row below; it is the title now, so the chip would be a
          duplicate — the sequence takes that slot as a muted reference instead. */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-strong truncate">{invoice.payerName ?? invoice.companyName ?? '—'}</span>
        <span className="ms-auto text-[11px] text-text-muted shrink-0" dir="ltr">{formatAmount(invoice.amount)}</span>
      </div>
      <DueDate dueDate={invoice.dueDate} status={invoice.status} className="text-xs" />
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        {/* A4 (D-I1/D-K1 class) — a read-only FACT on the row: status is action-driven from the
            detail. A chip, not a button — no false Edit promise, no stopPropagation dead zone over
            the row-open tap. */}
        <SystemChip label={t(`invoicesGrid.status.${invoice.status}`)} title={t('invoicesGrid.columns.status')} testId="invoice-status-chip" />
        <span className="text-[11px] text-text-muted truncate" dir="ltr">{t('invoicesGrid.columns.number')}: {invoice.number}</span>
      </div>
    </div>
  );
};

export const InvoiceGridRow: React.FC<{ invoice: InvoiceRow } & InvoiceRowCallbacks> = ({ invoice, selected, onOpenInvoice }) => {
  const { t } = useTranslation('common');
  return (
    <tr data-testid="invoice-row" tabIndex={0} onKeyDown={rowActivation(() => onOpenInvoice(invoice)).onKeyDown}
      className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`} onClick={() => onOpenInvoice(invoice)}>
      <td className="sticky z-10 bg-surface p-cell-y px-cell-x [inset-inline-start:0]">
        <span className="text-sm font-semibold text-text-strong truncate">{invoice.payerName || invoice.companyName || '—'}</span>
      </td>
      <td className="p-cell-y px-cell-x text-sm text-text-strong whitespace-nowrap" dir="ltr">{formatAmount(invoice.amount)}</td>
      <td className="p-cell-y px-cell-x whitespace-nowrap">
        <DueDate dueDate={invoice.dueDate} status={invoice.status} className="text-sm" />
      </td>
      <td className="p-cell-y px-cell-x whitespace-nowrap">
        <SystemChip label={t(`invoicesGrid.status.${invoice.status}`)} title={t('invoicesGrid.columns.status')} testId="invoice-status-chip" />
      </td>
      {/* the sequence, demoted: last column, muted, labelled Reference — an internal handle, not the
          record's identity (PM de-fiscalisation ruling, 2026-09-10). */}
      <td className="p-cell-y px-cell-x text-[11px] text-text-muted whitespace-nowrap" dir="ltr">{invoice.number}</td>
    </tr>
  );
};
